//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import moment from 'moment-timezone';
import { useDispatch } from 'react-redux';

import {
  addTrackedHashtags,
  removeTrackedHashtags,
} from '@apis/instagram-hashtag/instagramHashtagApi';
import {
  getHashtagsUsage,
  getHashtagsUsageForClient,
} from '@apis/instagram-hashtags-usage/instagramHashtagsUsageApi';
import { IConnectedBrandInstagramBusinessAccounts } from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import { getTrackedAndUsedHashtagLists } from '@hashtag-limits/converter/hashtagUsageApiDataConverter';
import {
  FilterLabels,
  FilterState,
} from '@hashtags/constants/HashtagsTableConstants';
import {
  groupHashtagsByRecentActivity,
  shouldCountTowardLimit,
} from '@hashtags/utils/groupHashtagsByRecentActivity';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import InstagramBusinessAccountId from '@models/InstagramBusinessAccountId';
import InstagramHashtag from '@models/InstagramHashtag';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { isDefined } from '@utils/filterPredicates';

export interface HashtagsByAccountData {
  businessAccountId: InstagramBusinessAccountId;
  usedHashtagCount: number;
}

/**
 * Fetches the number of hashtags used by each connected Instagram account.
 */
const fetchHashtagsUsageByAccounts = async (
  connectedAccounts: IConnectedBrandInstagramBusinessAccounts[] | undefined,
  init?: RequestInit,
): Promise<HashtagsByAccountData[]> => {
  if (!connectedAccounts?.length) {
    return [];
  }

  const accountsWithIds = connectedAccounts.filter((account) =>
    isDefined(account.businessAccountId),
  );
  const usagePromises = accountsWithIds.map(async (account) => {
    const usage = await getHashtagsUsage(account.businessAccountId, init);
    return {
      businessAccountId: account.businessAccountId,
      usedHashtagCount: usage.filter(shouldCountTowardLimit).length,
    };
  });

  return Promise.all(usagePromises);
};

/**
 * Query to fetch hashtags usage data by accounts
 */
export const useHashtagsUsageByAccountsQuery = (
  connectedAccounts: IConnectedBrandInstagramBusinessAccounts[] | undefined,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.TrackedSources,
      QueryKeyConstants.Hashtags,
      QueryKeyConstants.HashtagsUsage,
      connectedAccounts,
    ],
    queryFn: async ({ signal }) => {
      const data = await fetchHashtagsUsageByAccounts(connectedAccounts, {
        signal,
      });
      return (connectedAccounts ?? []).map((account) => ({
        businessAccountId: account.businessAccountId,
        usedHashtagCount:
          data.find((d) => d.businessAccountId === account.businessAccountId)
            ?.usedHashtagCount ?? 0,
      }));
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
    enabled: !!connectedAccounts,
  });
};

/**
 * Query to fetch hashtag usage data for multiple handles
 */
export const useMultiHandleHashtagsUsageQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.TrackedSources,
      QueryKeyConstants.Hashtags,
      QueryKeyConstants.HashtagsUsageMultiHandle,
    ],
    queryFn: async ({ signal }) => {
      const response = await getHashtagsUsageForClient({ signal });
      return response;
    },
    select: (response) => {
      const { otherUsedHashtags, trackedHashtags } =
        getTrackedAndUsedHashtagLists(response);

      // Group other used hashtags by recent activity
      const groupedByRecentUsage =
        groupHashtagsByRecentActivity(otherUsedHashtags);

      // Sort recently untracked hashtags by end date (most recent first)
      // and the rest alphabetically by name
      const sortedOtherHashtags = [
        ...(groupedByRecentUsage.true || []).sort((a, b) => {
          const dateA = a.endDate ? new Date(a.endDate).getTime() : 0;
          const dateB = b.endDate ? new Date(b.endDate).getTime() : 0;
          return dateB - dateA;
        }),
        ...(groupedByRecentUsage.false || []).sort((a, b) =>
          a.hashtag.localeCompare(b.hashtag),
        ),
      ];

      // Sort tracked hashtags by start date (most recent first),
      // then alphabetically by hashtag name if the date and time are the same
      const sortedTrackedHashtags = [...trackedHashtags].sort((a, b) => {
        const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
        const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;

        return dateB - dateA || a.hashtag.localeCompare(b.hashtag);
      });

      // Create properly formatted row data objects
      const processedTrackedHashtags = sortedTrackedHashtags.map((hashtag) => ({
        ...hashtag,
        descriptionText: FilterLabels[FilterState.TRACKING],
      }));

      const processedOtherUsedHashtags = sortedOtherHashtags.map((hashtag) => ({
        ...hashtag,
        descriptionText: hashtag.endDate
          ? `${FilterLabels[FilterState.UNTRACKED]} ${moment(hashtag.endDate).fromNow()}`
          : FilterLabels[FilterState.USED_ELSEWHERE],
      }));

      return {
        otherUsedHashtags,
        processedOtherUsedHashtags,
        processedTrackedHashtags,
        trackedHashtags,
        usedHashtagCount: response.filter(shouldCountTowardLimit).length,
      };
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

/**
 * Mutation to track a hashtag
 */
export const useTrackHashtagMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({
      businessAccountId,
      hashtagName,
    }: {
      businessAccountId: InstagramBusinessAccountId;
      hashtagName: string;
    }) => {
      await addTrackedHashtags({
        businessAccountId,
        hashtags: [InstagramHashtag(hashtagName)],
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.TrackedSources],
      });
    },
    onSuccess: (
      _,
      {
        hashtagName,
      }: { businessAccountId: InstagramBusinessAccountId; hashtagName: string },
    ) => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Hashtag added for tracking',
          type: ToastType.SUCCESS,
          message: `#${hashtagName} is now being tracked successfully.`,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Error Tracking Hashtag',
          type: ToastType.ERROR,
          message:
            'An unexpected error occurred while tracking the hashtag. Please try again.',
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
  });
};

/**
 * Mutation to untrack a hashtag
 */
export const useUntrackHashtagMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({
      businessAccountId,
      hashtagName,
    }: {
      businessAccountId: InstagramBusinessAccountId;
      hashtagName: string;
    }) => {
      await removeTrackedHashtags({
        businessAccountId,
        hashtags: [InstagramHashtag(hashtagName)],
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.TrackedSources],
      });
    },
    onSuccess: (
      _,
      {
        hashtagName,
      }: { businessAccountId: InstagramBusinessAccountId; hashtagName: string },
    ) => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Hashtag removed from tracking',
          type: ToastType.SUCCESS,
          message: `#${hashtagName} is successfully untracked.`,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Error Untracking Hashtag',
          type: ToastType.ERROR,
          message:
            'An unexpected error occurred while untracking the hashtag. Please try again.',
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
  });
};
